import { EventType } from "@/analytics/analytics-events";
import { SelectDateFilterPresetEventProperties } from "@/analytics/project-filter-events-properties";
import { selectLastCaptureDate } from "@/store/lightweight-elements-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  DatePicker,
  FaroButton,
  FaroChipFilter,
  FaroChipToggle,
  FaroMenu,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Divider } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useCallback, useState, type JSX } from "react";
import {
  DateFilterPreset,
  rangeForPreset,
  shouldSetLastCaptureDate,
  useSelectedPreset,
} from "./date-filter-presets";

type DateRangeFilterProps = {
  /** The currently selected start date */
  selectedStartDate?: Date;

  /** The currently selected end date */
  selectedEndDate?: Date;

  /** Callback to change the date range */
  onDateRangeChange(start?: Date, end?: Date): void;
};

const TODAY = new Date();

/** @returns A chip with a popup that can be used to select a date range */
export function DateRangeFilter({
  selectedStartDate,
  selectedEndDate,
  onDateRangeChange,
}: DateRangeFilterProps): JSX.Element {
  const lastCaptureDate = useAppSelector(selectLastCaptureDate);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const setDateFilterPreset = useCallback(
    (preset: DateFilterPreset) => {
      Analytics.track<SelectDateFilterPresetEventProperties>(
        EventType.selectDateFilterPreset,
        { preset },
      );
      onDateRangeChange(...rangeForPreset(preset, lastCaptureDate));
    },
    [onDateRangeChange, lastCaptureDate],
  );

  const hasActiveFilter = !!selectedStartDate || !!selectedEndDate;

  const selectedPreset = useSelectedPreset(
    selectedStartDate,
    selectedEndDate,
    lastCaptureDate,
  );
  const isLastCapture = selectedPreset === DateFilterPreset.lastCapture;
  const isThisWeek = selectedPreset === DateFilterPreset.thisWeek;
  const isThisMonth = selectedPreset === DateFilterPreset.thisMonth;

  const onFromDateChange = useCallback(
    (date: Date | undefined) => {
      if (shouldSetLastCaptureDate(date, selectedEndDate, lastCaptureDate)) {
        onDateRangeChange(date, lastCaptureDate);
        return;
      }
      onDateRangeChange(date, selectedEndDate);
    },
    [onDateRangeChange, selectedEndDate, lastCaptureDate],
  );

  const onToDateChange = useCallback(
    (date: Date | undefined) => {
      onDateRangeChange(selectedStartDate, date);
    },
    [onDateRangeChange, selectedStartDate],
  );

  const lastCaptureClicked = useCallback(() => {
    if (isLastCapture) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.lastCapture);
    }
  }, [isLastCapture, setDateFilterPreset, onDateRangeChange]);

  const thisWeekClicked = useCallback(() => {
    if (isThisWeek) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.thisWeek);
    }
  }, [isThisWeek, setDateFilterPreset, onDateRangeChange]);

  const thisMonthClicked = useCallback(() => {
    if (isThisMonth) {
      onDateRangeChange();
    } else {
      setDateFilterPreset(DateFilterPreset.thisMonth);
    }
  }, [isThisMonth, setDateFilterPreset, onDateRangeChange]);

  const clearAllClicked = useCallback(() => {
    Analytics.track(EventType.resetDateFilter);
    onDateRangeChange();
  }, [onDateRangeChange]);

  return (
    <Box ref={setAnchorEl} component="div">
      <FaroChipFilter
        label="Time of capture"
        selected={isPopoverOpen || hasActiveFilter}
        showCheckmark={hasActiveFilter}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      />

      {anchorEl && (
        <FaroMenu
          anchorEl={anchorEl}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          sx={{
            width: "500px",
            mt: 0.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "space-around", m: 1.5 }}
          >
            <FaroChipToggle
              label="Last capture"
              selected={isLastCapture}
              onClick={lastCaptureClicked}
            />
            <FaroChipToggle
              label="This week"
              selected={isThisWeek}
              onClick={thisWeekClicked}
            />
            <FaroChipToggle
              label="This month"
              selected={isThisMonth}
              onClick={thisMonthClicked}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: "space-around", my: 1.5, mx: 2 }}
          >
            <DatePicker
              label="From"
              date={selectedStartDate}
              maxDate={selectedEndDate ?? TODAY}
              onChange={onFromDateChange}
            />
            <DatePicker
              label="To"
              date={selectedEndDate}
              minDate={selectedStartDate}
              maxDate={TODAY}
              onChange={onToDateChange}
            />
          </Stack>

          <Divider />

          <FaroButton variant="ghost" onClick={clearAllClicked}>
            Clear all
          </FaroButton>
        </FaroMenu>
      )}
    </Box>
  );
}
